import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  Dot,
  Plus,
  RefreshCcw,
} from "lucide-react";
import { useRateVersion } from "../../../hooks";

type Version = {
  id: string;
  version: string;
  active?: boolean;
  effective: string;
  author: string;
  description: string;
  replaces?: string;
  changes: {
    type: "added" | "changed";
    category: string;
    title: string;
    from?: string;
    to: string;
  }[];
  snapshot: any;
};

export default function TariffVersionHistory() {
  const { id } = useParams();
  const { data: rawVersionData, isLoading } = useRateVersion(id as string);

  const versions: Version[] = useMemo(() => {
    const dataList = Array.isArray(rawVersionData) 
      ? rawVersionData 
      : [];

    return dataList.map((item: any) => ({
      id: String(item.id),
      version: item.version,
      active: item.isCurrent,
      effective: item.effectiveDate ?? 'NA',
      author: item.author?.name || 'NA',
      description: item.summary || 'NA',
      replaces: item.replaces || 'NA',
      changes: (item.changes || []).map((change: any) => ({
        type: change.changeType === 'added' ? 'added' : 'changed',
        category: change.category || '',
        title: change.field || '',
        from: change.from,
        to: change.to,
      })),
      snapshot: {}
    }));
  }, [rawVersionData]);

  const [selectedVersionId, setSelectedVersionId] = useState("");

  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      const activeVersion = versions.find(v => v.active);
      setSelectedVersionId(activeVersion ? activeVersion.id : versions[0].id);
    }
  }, [versions, selectedVersionId]);

  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) || versions[0],
    [selectedVersionId, versions]
  );

  if (isLoading) {
    return <div className="min-h-screen p-6">Loading version history...</div>;
  }

  if (!versions || versions.length === 0) {
    return <div className="min-h-screen p-6">No version history available.</div>;
  }

  return (
    <div className="min-h-screen p-6">
      <div className="grid grid-cols-12 gap-6">
        {/* LEFT SIDEBAR */}
        <div className="col-span-12 lg:col-span-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">
                Versions
              </h2>

              <div className="rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-semibold text-blue-600">
                {versions.find(v => v.active)?.version || ''} active
              </div>
            </div>

            <div className="relative ml-4 border-l border-slate-200">
              {versions.map((item, index) => {
                const selected = selectedVersionId === item.id;

                return (
                    <button
                    key={item.id}
                    onClick={() => setSelectedVersionId(item.id)}
                    className={`group relative mb-4 block w-full rounded-xl border p-4 text-left transition-all ${
                      selected
                        ? "border-blue-200 bg-blue-50"
                        : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {/* Timeline Dot */}
                    <div
                      className={`absolute -left-[23px] top-8 h-5 w-5 rounded-full border-4 border-white ${
                        selected ? "bg-slate-800" : "bg-blue-500"
                      }`}
                    />

                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-bold text-slate-900">
                        {item.version}
                      </h3>

                      {item.active && (
                        <div className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-sm font-semibold text-emerald-700">
                          Active
                        </div>
                      )}
                    </div>

                    <p className="mt-2 text-sm text-slate-500">
                      {item.effective}
                    </p>

                    <p className="mt-1 text-sm text-slate-600">
                      {item.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="col-span-12 space-y-5 lg:col-span-9">
          {/* HEADER */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {selectedVersion.version}
                  </h1>

                  {selectedVersion.active && (
                    <div className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-sm font-semibold text-emerald-700">
                      Active
                    </div>
                  )}

                  {selectedVersion.replaces && (
                    <div className="text-sm text-slate-500">
                      replaces{" "}
                      <span className="font-semibold text-slate-700">
                        {selectedVersion.replaces}
                      </span>
                    </div>
                  )}
                </div>

                <p className="mt-3 text-base text-slate-600">
                  {selectedVersion.description}
                </p>
              </div>

              <div className="flex gap-10">
                <div>
                  <p className="text-xs font-semibold tracking-[0.15em] text-slate-400">
                    Effective
                  </p>

                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedVersion.effective}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-[0.15em] text-slate-400">
                    Author
                  </p>

                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedVersion.author}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CHANGES */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                What changed{" "}
                {selectedVersion.replaces
                  ? `vs ${selectedVersion.replaces}`
                  : "in this version"}
              </h2>

              <div className="rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-600">
                {selectedVersion.changes.length} change
                {selectedVersion.changes.length > 1 ? "s" : ""}
              </div>
            </div>

            <div className="space-y-3">
              {selectedVersion.changes.map((change, index) => {
                const isAdded = change.type === "added";

                return (
                  <div
                    key={index}
                    className={`rounded-lg border p-4 ${
                      isAdded
                        ? "border-emerald-200 bg-emerald-50"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <div
                        className={`rounded-full border px-4 py-1 text-base font-semibold ${
                          isAdded
                            ? "border-emerald-200 bg-emerald-100 text-emerald-700"
                            : "border-blue-200 bg-blue-100 text-blue-700"
                        }`}
                      >
                        {isAdded ? "Added" : "Changed"}
                      </div>

                      <div className="bg-slate-200 px-2 py-0.5 text-xs font-bold tracking-[0.15em] text-slate-500">
                        {change.category}
                      </div>

                      <div className="text-base font-semibold text-slate-900">
                        {change.title}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                      {!isAdded && change.from && (
                        <>
                          <div className="bg-slate-200 px-2 py-0.5 text-xs font-bold tracking-[0.15em] text-slate-500">
                            From
                          </div>

                          <span className="text-red-500 line-through">
                            {change.from}
                          </span>

                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </>
                      )}

                      <div className="bg-slate-200 px-2 py-0.5 text-xs font-bold tracking-[0.15em] text-slate-500">
                        To
                      </div>

                      <span className="font-medium text-emerald-700">
                        {change.to}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}